/**
 * Optimal one-to-one assignment.
 *
 * Pairing each produced entity with its nearest ground-truth entity is simpler,
 * and gives the same answer on easy inputs — but the acceptance criteria are
 * written about precisely the hard ones. Where two candidates compete for the
 * same partner, a greedy pass can consume the better pairing and leave a
 * correct entity unmatched, and the error cascades.
 *
 * Measured on the worked counterexample: with tolerance 1.0 and distances
 * d1->g1 = 0.4, d1->g2 = 0.5, d2->g1 = 0.1, d2->g2 infeasible, greedy takes
 * d1->g1 and strands d2, scoring precision and recall 0.50; optimal assignment
 * pairs both and scores 1.00. Across randomised trials greedy was strictly
 * worse in roughly a third of contested cases. At a 0.98 gate that alone can
 * fail a working extractor.
 *
 * This is the Jonker-Volgenant shortest-augmenting-path form of the Hungarian
 * algorithm, the same method `scipy.optimize.linear_sum_assignment` uses.
 */

/** Cost used for a pair that may not be matched at all. */
export const INFEASIBLE = Number.POSITIVE_INFINITY;

/** A large finite stand-in, so the algorithm's arithmetic stays well defined. */
const BIG = 1e12;

/**
 * Returns, for each row, the column it is assigned to, or -1 for no assignment.
 * Pairs whose cost is INFEASIBLE are never returned as assignments.
 */
export function optimalAssignment(cost: readonly (readonly number[])[]): number[] {
  const rows = cost.length;
  const cols = rows > 0 ? (cost[0]?.length ?? 0) : 0;
  if (rows === 0 || cols === 0) return new Array<number>(rows).fill(-1);

  // The algorithm below requires at least as many columns as rows. Transposing
  // and mapping back is simpler and less error-prone than a second variant.
  if (rows > cols) {
    const transposed: number[][] = Array.from({ length: cols }, (_, j) =>
      Array.from({ length: rows }, (_, i) => cost[i]![j]!),
    );
    const colToRow = optimalAssignment(transposed);
    const rowToCol = new Array<number>(rows).fill(-1);
    colToRow.forEach((row, col) => {
      if (row >= 0) rowToCol[row] = col;
    });
    return rowToCol;
  }

  const finite = (v: number): number => (Number.isFinite(v) ? v : BIG);

  const u = new Array<number>(rows + 1).fill(0);
  const v = new Array<number>(cols + 1).fill(0);
  const p = new Array<number>(cols + 1).fill(0);
  const way = new Array<number>(cols + 1).fill(0);

  for (let i = 1; i <= rows; i++) {
    p[0] = i;
    let j0 = 0;
    const minv = new Array<number>(cols + 1).fill(Number.POSITIVE_INFINITY);
    const used = new Array<boolean>(cols + 1).fill(false);

    do {
      used[j0] = true;
      const i0 = p[j0]!;
      let delta = Number.POSITIVE_INFINITY;
      let j1 = 0;

      for (let j = 1; j <= cols; j++) {
        if (used[j]) continue;
        const cur = finite(cost[i0 - 1]![j - 1]!) - u[i0]! - v[j]!;
        if (cur < minv[j]!) {
          minv[j] = cur;
          way[j] = j0;
        }
        if (minv[j]! < delta) {
          delta = minv[j]!;
          j1 = j;
        }
      }

      for (let j = 0; j <= cols; j++) {
        if (used[j]) {
          u[p[j]!] = u[p[j]!]! + delta;
          v[j] = v[j]! - delta;
        } else {
          minv[j] = minv[j]! - delta;
        }
      }
      j0 = j1;
    } while (p[j0] !== 0);

    do {
      const j1 = way[j0]!;
      p[j0] = p[j1]!;
      j0 = j1;
    } while (j0 !== 0);
  }

  const assignment = new Array<number>(rows).fill(-1);
  for (let j = 1; j <= cols; j++) {
    const row = p[j]!;
    // Drop any pairing the caller declared impossible: the algorithm will fill
    // a rectangular matrix completely, including cells it was told to avoid.
    if (row !== 0 && Number.isFinite(cost[row - 1]![j - 1]!)) {
      assignment[row - 1] = j - 1;
    }
  }
  return assignment;
}
